import { useEffect, useState } from "react";
import axios from "axios";
import { X, Maximize2, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import TikTokIcon from "@/components/icons/TikTokIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIKTOK_URL = "https://www.tiktok.com/@stars.of.mpumalan";

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then((r) => setImages(r.data || []))
      .catch(() => setImages([]));
  }, []);

  if (!images.length) return null;

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-24 lg:py-36 px-6 lg:px-10 bg-[#0a0a0a]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16 fade-up">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-red-600" />
              <span className="text-xs uppercase tracking-[0.3em] text-red-600">
                Inside the Academy
              </span>
            </div>
            <h2
              data-testid="gallery-title"
              className="font-heading uppercase text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight"
            >
              Moments. Made <br />
              <span className="text-red-600">In Witbank.</span>
            </h2>
          </div>
          <p className="max-w-md text-white/60 leading-relaxed">
            Match days, training drills, lifelong friendships — a glimpse into
            life at Stars of Mpumalanga.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-3 md:gap-4">
          {images.map((img, idx) => {
            // Asymmetric tetris layout — vary spans
            const span =
              idx % 7 === 0
                ? "md:row-span-2 col-span-2"
                : idx % 5 === 0
                ? "md:col-span-2"
                : "";
            return (
              <button
                key={img.id}
                data-testid={`gallery-thumb-${img.id}`}
                onClick={() => setActive(img)}
                className={`group relative overflow-hidden rounded-lg border border-white/10 bg-neutral-900 fade-up ${span}`}
                style={{ transitionDelay: `${idx * 60}ms` }}
              >
                <img
                  src={img.src}
                  alt={img.caption}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-transparent opacity-60 group-hover:opacity-90 transition-opacity" />
                <div className="absolute top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Maximize2 className="h-3.5 w-3.5" />
                </div>
                <div className="absolute bottom-3 left-3 right-3 text-left text-white text-xs uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                  {img.caption}
                </div>
              </button>
            );
          })}
        </div>

        {/* TikTok CTA banner — links to live academy content */}
        <div
          data-testid="tiktok-cta"
          className="mt-10 lg:mt-14 relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-neutral-950 via-black to-red-950/40 p-6 lg:p-8 fade-up"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-2 flex lg:justify-center">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-black">
                <TikTokIcon className="h-7 w-7" />
              </span>
            </div>
            <div className="lg:col-span-7">
              <p className="text-[10px] uppercase tracking-[0.3em] text-red-600 mb-2">
                More on TikTok
              </p>
              <h3 className="font-heading uppercase text-2xl lg:text-3xl tracking-tight leading-none mb-2">
                Catch every match, drill & moment.
              </h3>
              <p className="text-sm text-white/60 leading-relaxed">
                Our full photo & video gallery lives on TikTok —
                <span className="text-white"> @stars.of.mpumalan</span>. Follow
                for weekly highlights, training clips and player spotlights.
              </p>
            </div>
            <div className="lg:col-span-3 flex lg:justify-end">
              <Button
                asChild
                data-testid="tiktok-follow-button"
                className="bg-white hover:bg-red-600 hover:text-white text-black font-heading uppercase tracking-widest rounded-full px-6 h-12 text-xs"
              >
                <a
                  href={TIKTOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2"
                >
                  <TikTokIcon className="h-4 w-4" />
                  Follow on TikTok
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="gallery-lightbox"
          className="max-w-5xl bg-black border-white/10 p-0"
        >
          {active && (
            <div className="relative">
              <DialogTitle className="sr-only">{active.caption}</DialogTitle>
              <img
                src={active.src}
                alt={active.caption}
                className="w-full max-h-[80vh] object-contain bg-black"
              />
              <button
                onClick={() => setActive(null)}
                aria-label="Close"
                className="absolute top-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/70 text-white hover:bg-red-600 hover:text-white transition"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent text-white text-sm uppercase tracking-widest">
                {active.caption}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
